import { c as _c } from "react/compiler-runtime";
import { appWithTranslation } from "next-i18next";
import App, { AppContext as ApplicationContext, AppProps, AppType } from "next/app";
import Head from "next/head";
import { useRouter } from "@lib/hooks/useRouter";
import { useEffect } from "react";
import "tailwind-base/theme.css";
import "../styles/alice-carousel.css";
import "../styles/cookiebot.css";
import "../styles/global.css";
import "../styles/push-carousel.css";
import ErrorBoundary from "@components/ErrorBoundary";
import Fathom from "@components/Fathom";
import { disableTracking, FeatureFlagsProvider, initTracking, isTrackingEnabled, logger, umami } from "@justhome/common";
import { configureCommon } from "@lib/commonConfig";
import { GlobalContext } from "@lib/context/global/context";
import { splitLocale } from "@lib/i18n/locale";
import useCookiebotRenewal from "@lib/hooks/useCookiebotRenewal";
import useIsTest from "@lib/hooks/useIsTest";
import useLanguage from "@lib/hooks/useLanguage";
import getGlobalData from "@lib/strapi/getGlobalData";
import { parseUTM, saveUTM } from "@lib/utm";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import LanguageModal from "@components/LanguageModal";
import Providers from "@components/Providers";
import { ApplicationProvider } from "@modules/application/context/ApplicationContext";
configureCommon();
initTracking();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});
const trackExperimentViewed = (experimentKey: string, variationId: number) => {
  umami.trackEvent(experimentKey, {
    variationId
  });
};
interface MyAppProps {
  siteData: any;
  random: number;
}
function MyApp(t0) {
  const $ = _c(24);
  const {
    Component,
    pageProps
  } = t0;
  const router = useRouter();
  useCookiebotRenewal();
  const isTest = useIsTest();
  const language = useLanguage();
  let t1;
  let t2;
  if ($[0] !== language) {
    t1 = () => {
      document.documentElement.lang = language;
    };
    t2 = [language];
    $[0] = language;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== router.query) {
    t3 = () => {
      if (router.query) {
        const utm = parseUTM(router.query);
        saveUTM(utm);
      }
    };
    t4 = [router.query];
    $[3] = router.query;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== isTest) {
    t5 = () => {
      let interval;
      if (isTest) {
        interval = window.setInterval(() => {
          ;
          try {
            if (window.cookiebot) {
              window.cookiebot.hide();
              window.cookiebot.withdraw();
            }
            if (isTrackingEnabled()) {
              disableTracking();
            }
          } catch (t7) {
            const error = t7;
            logger.error(`Error in ${MyApp.name} component.`, error);
          }
          if (window.cookiebot && window.fathom) {
            clearInterval(interval);
          }
        }, 500);
      }
      return () => {
        clearInterval(interval);
      };
    };
    t6 = [isTest];
    $[6] = isTest;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Fathom />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Head><meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=1" /></Head>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = Component as any;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LanguageModal />;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== Component || $[13] !== pageProps) {
    t11 = <Component {...pageProps} />;
    $[12] = Component;
    $[13] = pageProps;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t9.applicationContextInitialValue) {
    t12 = <ApplicationProvider initialState={t9.applicationContextInitialValue}>{t10}{t11}</ApplicationProvider>;
    $[15] = t11;
    $[16] = t9.applicationContextInitialValue;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== pageProps || $[19] !== t12) {
    t13 = <QueryClientProvider client={queryClient}><GlobalContext.Provider value={pageProps}>{t12}</GlobalContext.Provider></QueryClientProvider>;
    $[18] = pageProps;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== router.asPath || $[22] !== t13) {
    t14 = <div>{t7}{t8}<ErrorBoundary><Providers><FeatureFlagsProvider url={router.asPath} onExperimentViewed={trackExperimentViewed}>{t13}</FeatureFlagsProvider></Providers></ErrorBoundary></div>;
    $[21] = router.asPath;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  return t14;
}
MyApp.getInitialProps = async (appContext: ApplicationContext) => {
  // Calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);
  const {
    locale
  } = splitLocale(appContext.ctx.asPath ?? "/");
  const siteData = await getGlobalData(locale);
  return {
    ...appProps,
    pageProps: {
      siteData,
      random: Math.random()
    }
  };
};

// appWithTranslation does not like generic AppProps
export default appWithTranslation(MyApp) as AppType;